import cn from 'classnames';
import { type ChangeEvent, type FC, useCallback, useId } from 'react';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import Icon from '@/components/UI/Icon';
import { errorToast, formatBytes, trunc } from '@/helpers';
import { useFileDropzone } from '@/support/Hooks/dropzone/useFileDropzone';

interface FileUploadProps {
  limit?: number;
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
  disabled?: boolean;
  allowedExtensions?: Array<string>;
  testId?: string;
}

const hasValidExtension = (fileName: string, allowedExtensions: Array<string>): boolean => {
  if (_isEmpty(allowedExtensions)) {
    return true;
  }
  const match = /\.([^.]+)$/.exec(fileName);
  if (match === null) {
    return false;
  }
  return allowedExtensions.includes(`.${match[1].trim()}`);
};

const FileUpload: FC<FileUploadProps> = ({
  limit,
  files,
  setFiles,
  disabled = false,
  allowedExtensions = [],
  testId = 'file-upload-drop-zone',
}) => {
  return (
    <>
      {_isEmpty(files) ? (
        <FileDropTarget
          limit={limit}
          setFiles={setFiles}
          disabled={disabled}
          allowedExtensions={allowedExtensions}
          testId={testId}
        />
      ) : (
        <FilledField setFiles={setFiles} files={files} />
      )}
    </>
  );
};

interface FileDropTargetProps {
  limit?: number;
  setFiles: (files: Array<File>) => void;
  disabled: boolean;
  allowedExtensions: Array<string>;
  testId: string;
}

const FileDropTarget: FC<FileDropTargetProps> = ({ limit, setFiles, disabled, allowedExtensions, testId }) => {
  const applyPickedFiles = useCallback(
    (fileList: FileList | null) => {
      if (fileList === null || fileList.length === 0) {
        return;
      }
      let next = Array.from(fileList);
      if (_isNotEmpty(allowedExtensions)) {
        const invalid = next.find((f) => {
          return !hasValidExtension(f.name, allowedExtensions);
        });
        if (_notNil(invalid)) {
          errorToast(
            <>
              <p>
                <b>{invalid.name}</b>
              </p>
              <p>is not a supported file type</p>
            </>
          );
          return;
        }
      }
      if (_notNil(limit) && limit > 0) {
        next = next.slice(0, limit);
      }
      setFiles(next);
    },
    [allowedExtensions, limit, setFiles]
  );

  const { isDraggingOver, dropzoneProps } = useFileDropzone({ onFiles: applyPickedFiles, disabled });
  const isActive = !disabled && isDraggingOver;

  return (
    <div
      data-testid={testId}
      className={cn('ui__file__upload ba b--dashed f6 tc', {
        ui__disabled: disabled,
        'b--blue': isActive,
        'b--moon-gray': !isActive,
      })}
      {...dropzoneProps}
    >
      <p className="ma0">
        Drag and drop or{' '}
        <ChooseFileLink setFiles={setFiles} allowedExtensions={allowedExtensions} limit={limit} disabled={disabled} />
      </p>
    </div>
  );
};

interface ChooseFileLinkProps {
  setFiles: (files: Array<File>) => void;
  allowedExtensions: Array<string>;
  limit?: number;
  disabled: boolean;
}

const ChooseFileLink: FC<ChooseFileLinkProps> = ({ setFiles, allowedExtensions, limit, disabled }) => {
  const inputId = useId();

  const captureFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    if (list === null || list.length === 0) {
      return;
    }
    let next = Array.from(list);
    if (_isNotEmpty(allowedExtensions)) {
      const invalid = next.find((f) => {
        return !hasValidExtension(f.name, allowedExtensions);
      });
      if (_notNil(invalid)) {
        errorToast(
          <>
            <p>
              <b>{invalid.name}</b>
            </p>
            <p>is not a supported file type</p>
          </>
        );
        e.target.value = '';
        return;
      }
    }
    if (_notNil(limit) && limit > 0) {
      next = next.slice(0, limit);
    }
    setFiles(next);
    e.target.value = '';
  };

  return (
    <label htmlFor={inputId} className="link dib pointer ma0">
      browse your computer.
      <input
        id={inputId}
        type="file"
        className="dn"
        disabled={disabled}
        onChange={captureFiles}
        accept={_isNotEmpty(allowedExtensions) ? allowedExtensions.join(',') : undefined}
      />
    </label>
  );
};

interface FilledFieldProps {
  files: Array<File>;
  setFiles: (files: Array<File>) => void;
}

const FilledField: FC<FilledFieldProps> = ({ files, setFiles }) => {
  const handleRemoveFile = (index: number) => {
    setFiles(
      files.filter((_, i) => {
        return i !== index;
      })
    );
  };

  return (
    <ul className="ui__file__upload__filledField list ma0">
      {!_isEmpty(files) &&
        files.map((f, i) => (
          <li
            className={cn('flex justify-between items-center', { mt2: i !== 0 })}
            key={`${f.name}-${String(f.lastModified)}-${String(i)}`}
          >
            <span className="basier-med bg-light-silver br2 mr2 near-black f6 w-100">
              {trunc(f.name, 20)} ({formatBytes(f.size)})
            </span>
            <button
              type="button"
              className="pointer hover-near-black bn bg-transparent pa0"
              onClick={() => {
                handleRemoveFile(i);
              }}
            >
              <Icon icon="close" width="10" height="10" className="dark-gray" />
            </button>
          </li>
        ))}
    </ul>
  );
};

export default FileUpload;
